import React, { createContext, useContext, useEffect, useState } from "react";
import MaintenanceModeView from "./views/MaintenanceModeView";
import AsyncStorage from "@react-native-async-storage/async-storage";
import Sentry from "./utils/sentry";
import { StyleSheet, View } from "react-native";
import Loader from "./components/common/status/Loader/Loader";
import axios from "axios";

export type DomainConfig = {
  hasTrial: boolean;
  companionName: string;
  key: string;
  isLMS: boolean;
  isLTI: boolean;
  ltiLaunchUrl?: string;
  isAxioOffering: boolean;
  isCompanionMode: boolean;
  isSuggestedInsights: boolean;
  axioSchools: string[];
  primaryColor: string;
  gameMenuBg: string;
  gameMenuTrack: string;
  gameMenuProgress: string;
  gameMenuCardBg: string;
  logo: string;
  proLogo: string;
  miniLogo: string;
  showMiniLogo: boolean;
  scope: string[];
  additionalScope?: string[]; // { name: string; externalURL: string; index: number }
  termsOfServiceLink: string;
  allowFormSignup: boolean;
  showFeedback: boolean;
  upgradeModalBody: string;
  isFreemium: boolean;
  hasEmailVerification: boolean;
  hasProFlow: boolean;
  hasDegrees: boolean;
  hasIntegrationsEnabled: boolean;
  hasCourseEnrollment: boolean;
  hasLearningTopicsInChat: boolean;
  officialTranscriptsUrl: string;
  financialInformationUrl: string;
  helpCenterUrl: string;
  schoolResourcesUrl: string;
  reportProblemUrl: string;
  scheduleAdvisorCallUrl: string;
  submitHelpTicketLink?: string;
  hasSAMLSSO: boolean;
  hasTurnBasedAssessments: boolean;
  oralAssessmentV2Enabled?: boolean;
  useILEGraphV2: boolean;
  requireSSO?: boolean;
  imageGenerationSupportFlag: boolean;
  deepResearchSupportFlag?: boolean;
  lmsDirectFacultyMode?: boolean;
  gradebookEnabled?: boolean;
  courseModernizerEnabled?: boolean;
};

type ExtendedDomainConfig = DomainConfig & {
  currentDomain: string;
  isConfigReady: boolean;
};

type AppConfigProviderProps = {
  children: React.ReactNode;
  /**
   * Optional override to avoid fetching config from the network.
   * Used by Storybook/tests to prevent loading against `localhost:6006`.
   */
  appConfig?: DomainConfig;
  /**
   * Optional override for the current domain value exposed via context.
   */
  currentDomain?: string;
};

const AppConfigContext = createContext<ExtendedDomainConfig | null>(null);

export const AppConfigProvider = ({
  children,
  appConfig: appConfigOverride,
  currentDomain: currentDomainOverride,
}: AppConfigProviderProps) => {
  const initialDomain = typeof window !== "undefined" ? window.location.host : "";
  const resolvedDomain = currentDomainOverride ?? initialDomain;

  // If we were given an explicit config (storybook/tests), skip all network loading.
  if (appConfigOverride) {
    return (
      <AppConfigContext.Provider value={{ ...appConfigOverride, currentDomain: resolvedDomain, isConfigReady: true }}>
        {children}
      </AppConfigContext.Provider>
    );
  }

  const [currentDomain, setCurrentDomain] = useState(resolvedDomain);
  const [appConfig, setAppConfig] = useState<DomainConfig | null>(null);
  const [cachedLtiLaunchUrl, setCachedLtiLaunchUrl] = useState<string | null>(null);
  const [maintenanceModeStatus, setMaintenanceModeStatus] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const LTI_LAUNCH_URL_STORAGE_KEY = "axio_lti_launch_url";

  // Fetching the domain from AsyncStorage
  useEffect(() => {
    const fetchDomain = async () => {
      try {
        const savedDomain = await AsyncStorage.getItem("domain");
        if (savedDomain) {
          setCurrentDomain(savedDomain);
        }
        const savedLtiLaunchUrl = await AsyncStorage.getItem(LTI_LAUNCH_URL_STORAGE_KEY);
        if (savedLtiLaunchUrl) {
          setCachedLtiLaunchUrl(savedLtiLaunchUrl);
        }
        if (typeof window !== "undefined" && window.localStorage) {
          const localUrl = window.localStorage.getItem(LTI_LAUNCH_URL_STORAGE_KEY);
          if (localUrl) {
            setCachedLtiLaunchUrl(localUrl);
          }
        }
      } catch (error) {
        Sentry.captureException(error);
      }
    };
    fetchDomain();
  }, []);

  useEffect(() => {
    const fetchAppConfig = async () => {
      setIsLoading(true);
      try {
        const url = `https://${currentDomain}/api/config`;

        const response = await axios.get(url, {
          headers: {
            "Content-Type": "application/json",
            "X-User-Domain": currentDomain,
          },
        });

        const config = response.data;
        if (config?.ltiLaunchUrl) {
          setCachedLtiLaunchUrl(config.ltiLaunchUrl);
          AsyncStorage.setItem(LTI_LAUNCH_URL_STORAGE_KEY, config.ltiLaunchUrl);
          if (typeof window !== "undefined" && window.localStorage) {
            window.localStorage.setItem(LTI_LAUNCH_URL_STORAGE_KEY, config.ltiLaunchUrl);
          }
        } else {
          setCachedLtiLaunchUrl(null);
          AsyncStorage.removeItem(LTI_LAUNCH_URL_STORAGE_KEY);
          if (typeof window !== "undefined" && window.localStorage) {
            window.localStorage.removeItem(LTI_LAUNCH_URL_STORAGE_KEY);
          }
        }
        setAppConfig(config);

        const maintenanceResponse = await axios.get(`${url}/maintenance`, {
          headers: {
            "Content-Type": "application/json",
            "X-User-Domain": currentDomain,
          },
        });

        setMaintenanceModeStatus(maintenanceResponse.data.maintenanceModeStatus === "true");
      } catch (error) {
        Sentry.captureException(error);
      } finally {
        setIsLoading(false);
      }
    };

    if (currentDomain) {
      fetchAppConfig();
    }
  }, [currentDomain]);
  // No need for useState for appConfig or isLoading here

  if (isLoading || !appConfig?.companionName) {
    return (
      <View style={styles.loaderContainer}>
        <Loader />
      </View>
    );
  }

  return (
    <AppConfigContext.Provider
      value={{
        ...appConfig,
        ltiLaunchUrl: appConfig?.ltiLaunchUrl ?? cachedLtiLaunchUrl ?? undefined,
        currentDomain,
        isConfigReady: !isLoading && !!appConfig,
      }}>
      {maintenanceModeStatus ? <MaintenanceModeView logo={appConfig.logo} /> : children}
    </AppConfigContext.Provider>
  );
};

export const useAppConfig = () => useContext(AppConfigContext);

const styles = StyleSheet.create({
  loaderContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
});
